import { ButtonPrimeReact, DropdownPrimeReact } from "@finanedi.es/storybook"
import { useQuery } from "@tanstack/react-query"
import { postDireccion } from "api/Direccion.service"
import { getPoblacionByCodigoPostal } from "api/Poblacion.service"
import Modal from "common/components/Modal/Modal"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { FormikProps, withFormik } from "formik"
import { TFunction } from "i18next"
import { InputText } from "primereact/inputtext"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"

interface FormValues {
  direccion: string | null
  codigoPostal: string | undefined
  poblacion: string | null
  poblacionId: number | undefined
  provincia: string | undefined
  provinciaId: number | undefined
  empresaActivaId: string
}

interface OuterProps {
  visible: boolean
  onHide: () => void
  empresaActivaId: string
  t: TFunction
  setFieldValueForm: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: $TSMigrationFixMe,
    shouldValidate?: boolean
  ) => Promise<void>
  valuesForm: $TSMigrationFixMe
}

const DireccionSocialForm = ({
  visible,
  errors,
  values,
  handleSubmit,
  setFieldValue,
}: FormikProps<FormValues> & Partial<OuterProps>) => {
  const { t } = useTranslation()
  const {
    data: poblacionesByCodigoPostal,
    isLoading: poblacionesByCodigoPostalIsLoading,
  } = useQuery({
    queryKey: ["poblacionById", values.codigoPostal],
    enabled: Boolean(
      values.codigoPostal && String(values.codigoPostal).length === 5
    ),
    queryFn: async () => {
      const data = await getPoblacionByCodigoPostal(String(values.codigoPostal))
      if (data.length > 0) {
        setFieldValue("poblacion", data[0].nombre, !!errors.poblacion)
        setFieldValue("poblacionId", data[0].id)
        setFieldValue("provincia", data[0].provinciaNombre)
        setFieldValue("provinciaId", data[0].provinciaId)
      }
      return data
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })

  return (
    <Modal
      size='normal'
      draggable
      closable={false}
      maskClassName='!tw-z-40 !tw-rounded-md'
      header={`${t("crear.formDireccion.addDireccion")}`}
      className='tw-rounded-lg'
      visible={visible}
    >
      <div className='tw-w-full tw-flex tw-flex-col tw-gap-3'>
        <span className='tw-text-sm'>{t("crear.formDireccion.infoText")}</span>
        <div className='tw-w-full tw-flex tw-flex-col'>
          <label className='tw-text-sm'>
            {t("crear.formDireccion.direccion")}
          </label>
          <InputText
            value={values.direccion ?? ""}
            onChange={(e) =>
              setFieldValue("direccion", e.target.value, !!errors.direccion)
            }
            className='tw-w-full'
          />
          <span
            data-is-error={!!errors.direccion}
            className='data-[is-error=false]:tw-hidden tw-text-danger-20 tw-text-sm tw-mt-1 tw-px-1'
          >
            {errors.direccion}
          </span>
        </div>
        <div className='tw-w-full tw-flex tw-flex-row tw-gap-4'>
          <div className='tw-w-1/2 tw-flex tw-flex-col'>
            <label className='tw-text-sm'>
              {t("crear.formDireccion.codPostal")}
            </label>
            <InputText
              value={values.codigoPostal ?? ""}
              onChange={(e) => {
                const val = e.target.value
                if (/^\d{0,5}$/.test(val)) {
                  setFieldValue("codigoPostal", val, true)
                }
              }}
              className='tw-w-full'
            />
            <span
              data-is-error={!!errors.codigoPostal}
              className='data-[is-error=false]:tw-hidden tw-text-danger-20 tw-text-sm tw-mt-1 tw-px-1'
            >
              {errors.codigoPostal}
            </span>
          </div>
          <div className='tw-w-1/2 tw-flex tw-flex-col'>
            <label className='tw-text-sm '>
              {t("crear.formDireccion.poblacion")}
            </label>
            <DropdownPrimeReact
              disabled={!values.codigoPostal}
              options={poblacionesByCodigoPostal}
              loading={poblacionesByCodigoPostalIsLoading}
              optionLabel='nombre'
              optionValue='nombre'
              value={values.poblacion ?? ""}
              onChange={(e) => {
                setFieldValue("poblacion", e.target.value, !!errors.poblacion)
                const poblacion = poblacionesByCodigoPostal?.find(
                  (p) => p.nombre === e.target.value
                )
                setFieldValue("poblacionId", poblacion?.id)
                setFieldValue("provincia", poblacion?.provinciaNombre)
                setFieldValue("provinciaId", poblacion?.provinciaId)
              }}
              className='tw-w-full !tw-rounded-md'
            />
            <span
              data-is-error={!!errors.poblacion}
              className='data-[is-error=false]:tw-hidden tw-text-danger-20 tw-text-sm tw-mt-1 tw-px-1'
            >
              {errors.poblacion}
            </span>
          </div>
        </div>
        <div className='tw-w-full tw-flex tw-justify-end'>
          <ButtonPrimeReact
            className='tw-bg-blue-500 tw-text-white tw-p-2 tw-rounded'
            onClick={() => {
              handleSubmit()
            }}
          >
            {t("crear.formDireccion.saveButton")}
          </ButtonPrimeReact>
        </div>
      </div>
    </Modal>
  )
}

const FormDireccionFactura = withFormik<OuterProps, FormValues>({
  mapPropsToValues: ({ empresaActivaId }) => ({
    direccion: null,
    codigoPostal: undefined,
    poblacion: null,
    poblacionId: undefined,
    provincia: undefined,
    provinciaId: undefined,
    empresaActivaId,
  }),
  validateOnBlur: false,
  validateOnChange: false,
  validationSchema: ({ t }: { t: TFunction }) =>
    Yup.object().shape({
      direccion: Yup.string()
        .nullable()
        .required(`${t("crear.formDireccion.errors.direccion")}`),
      codigoPostal: Yup.string()
        .typeError(`${t("crear.formDireccion.errors.codPostalNum")}`)
        .required(`${t("crear.formDireccion.errors.codPostalReq")}`)
        .test(
          "len",
          `${t("crear.formDireccion.errors.codPostalDig")}`,
          (val) => val !== undefined && val.length === 5
        ),
      poblacion: Yup.string()
        .nullable()
        .required(`${t("crear.formDireccion.errors.poblacion")}`),
    }),
  handleSubmit: async (values, { props }) => {
    const direccion = {
      calle: values.direccion,
      codigoPostal: values.codigoPostal,
      esFiscal: true,
      poblacionExtranjera: null,
      poblacionId: values.poblacionId,
      poblacionNombre: values.poblacion,
      provinciaId: values.provinciaId,
      provinciaNombre: values.provincia,
      empresaId: values.empresaActivaId,
    }

    const resp = await postDireccion(direccion)
    if (resp.status === STATUSCORRECT) {
      if (props?.valuesForm?.librador?.id === props?.empresaActivaId) {
        props.setFieldValueForm("librador.direccion", direccion)
      } else {
        props.setFieldValueForm("librado.direccion", direccion)
      }
      props.setFieldValueForm("direccion", resp.data)
      props.onHide()
    }
  },
})(DireccionSocialForm)

export default FormDireccionFactura
